import { useEffect, useState } from "react";
import {
  getAllPromoters, getAttendanceByMonth, getAllAttendanceByMonth,
  getPendingAppeals, getAllAppeals, approveAppeal, rejectAppeal, addAuditLog, getHolidays,
  getPendingLeaves, approveLeave, rejectLeave,
  getPendingOvertime, approveOvertime, deleteOvertime, getOvertimeByMonth, setOvertimeByAdmin,
  setPaidLeave, addEventBonus, getEventBonuses, deleteEventBonus, deleteAttendance, setManualWorkDay,
} from "../../firebase/services";
import { format, getDaysInMonth } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";
import { DAY_TYPES, OT_KINDS, isCreditedDay, ATT_STATUS, getAttendanceStatus, isPendingLeave, countPaidDays } from "../../firebase/schema";
import { exportAttendanceMatrixXLSX } from "../../utils/exportAttendanceExcel";
import { usePayrollConfig } from "../../hooks/usePayrollConfig";

const exportAllToCSV = async (promoters, month, getAllFn) => {
  const allRecords = await getAllFn(month);
  // Giải trình của tháng → điền vào cột theo đúng ngày
  const monthAppeals = await getAllAppeals(month).catch(() => []);
  const apMap = {};
  monthAppeals.forEach(a => { apMap[`${a.promoterId}_${a.date}`] = a; });
  const apText = (uid, date) => {
    const a = apMap[`${uid}_${date}`];
    if (!a) return "";
    return `${APPEAL_STATUS_VI[a.status]?.label || a.status}: ${a.reason || ""}`;
  };
  const rows = [];
  const header = ["Promoter", "Cửa hàng", "Ngày", "Check-in", "Check-out", "Tổng giờ", "Ngày lễ", "Ghi chú", "Giải trình"];
  rows.push(header);

  promoters.forEach(p => {
    const pRecords = allRecords
      .filter(r => r.promoterId === p.uid)
      .sort((a, b) => a.date.localeCompare(b.date));

    rows.push([]);
    rows.push([`=== ${p.name} – ${p.shopName} ===`]);

    if (pRecords.length === 0) {
      rows.push([p.name, p.shopName, "(Không có dữ liệu)"]);
    } else {
      pRecords.forEach(r => rows.push([
        p.name, p.shopName, r.date,
        r.checkinTime || "", r.checkoutTime || "",
        r.totalHours != null ? r.totalHours : "",
        r.isHoliday ? `Lễ ×${r.holidayMultiplier || 3}` : "",
        r.appealApproved ? "Giải trình OK" : (r.totalHours && r.totalHours < 7 ? "Thiếu giờ" : ""),
        apText(p.uid, r.date),
      ]));
      const worked = pRecords.filter(isCreditedDay);
      const daysWorked = worked.reduce((a, r) => a + (r.dayCredit ?? 1), 0);
      const holidayDays = worked.filter(r => r.isHoliday).reduce((a, r) => a + (r.dayCredit ?? 1), 0);
      const totalHours = pRecords.reduce((a, r) => a + (r.totalHours || 0), 0);
      rows.push(["", "", "Tổng kết:", `${daysWorked} công`, "", `${totalHours.toFixed(1)}h`, `${holidayDays} công lễ`]);
    }
  });

  const csvContent = rows
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cham-cong_tat-ca_${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const exportToCSV = (promoterName, month, records) => {
  const header = ["Ngày", "Check-in", "Check-out", "Tổng giờ", "Ngày lễ", "Hệ số lễ"];
  const rows = records.map(r => [
    r.date, r.checkinTime || "", r.checkoutTime || "",
    r.totalHours != null ? r.totalHours : "",
    r.isHoliday ? "Có" : "", r.isHoliday ? (r.holidayMultiplier || 3) : "",
  ]);
  const worked = records.filter(isCreditedDay);
  const daysWorked = worked.reduce((a, r) => a + (r.dayCredit ?? 1), 0);
  const holidayDays = worked.filter(r => r.isHoliday).reduce((a, r) => a + (r.dayCredit ?? 1), 0);
  const totalHours = records.reduce((a, r) => a + (r.totalHours || 0), 0);
  rows.push([], ["Tổng kết"], ["Công đi làm", daysWorked],
    ["Công lễ (×3)", holidayDays], ["Tổng giờ làm", totalHours.toFixed(1) + "h"]);
  const csvContent = [header, ...rows]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cham-cong_${promoterName.replace(/\s+/g, "-")}_${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

function PhotoModal({ record, onClose, footer = null }) {
  if (!record) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 620, maxWidth: "96vw" }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Ảnh chấm công – {record.date?.slice(8)}/{record.date?.slice(5,7)}/{record.date?.slice(0,4)}</div>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {[
            { label: "Check-in", time: record.checkinTime, photo: record.checkinPhoto },
            { label: "Check-out", time: record.checkoutTime, photo: record.checkoutPhoto },
          ].map(({ label, time, photo }) => (
            <div key={label}>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 6, fontWeight: 500 }}>
                {label} {time ? `– ${time}` : "–"}
              </div>
              {photo
                ? <img src={photo} alt={label} style={{ width: "100%", borderRadius: 10, aspectRatio: "4/3", objectFit: "cover", border: "0.5px solid rgba(0,0,0,0.1)" }} />
                : <div style={{ background: "#f5f4f1", borderRadius: 10, aspectRatio: "4/3", display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb", fontSize: 12 }}>
                    Không có ảnh
                  </div>
              }
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
          {record.totalHours && <span style={{ fontSize: 12, color: "#555" }}>Tổng: <b>{record.totalHours}h</b></span>}
          {record.isHoliday && <span className="badge badge-amber">Lễ ×{record.holidayMultiplier}</span>}
          {record.totalHours && record.totalHours < 7 && !record.appealApproved && <span className="badge badge-red">Thiếu giờ</span>}
          {record.appealApproved && <span className="badge badge-blue">Đã duyệt giải trình</span>}
        </div>
        {footer}
      </div>
    </div>
  );
}

// Admin nhập thẳng giờ tăng ca / làm ngày lễ / làm ngày nghỉ tuần cho một ngày.
// Giờ admin nhập được dùng đúng như nhập (đối chiếu bảng lương agency, báo qua chat…).
function OtAdminPanel({ promoter, dateStr, isHoliday, existing, onDone }) {
  const { t } = useLanguage();
  const kinds = isHoliday
    ? [[OT_KINDS.HOLIDAY, t("ot.kind_holiday")]]
    : [[OT_KINDS.EXTRA, t("ot.kind_extra")], [OT_KINDS.WEEKLY_OFF, t("ot.kind_weekly_off")]];
  const initKind = isHoliday ? OT_KINDS.HOLIDAY
    : existing?.kind === OT_KINDS.WEEKLY_OFF ? OT_KINDS.WEEKLY_OFF : OT_KINDS.EXTRA;
  const [kind, setKind] = useState(initKind);
  const [hours, setHours] = useState(existing?.hours ?? (isHoliday ? 8 : 2));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const run = async (fn) => {
    setBusy(true); setErr("");
    try { await fn(); onDone(); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const save = () => {
    const h = Number(hours);
    if (!(h > 0 && h <= 24)) { setErr(t("ot.admin_hours_err")); return; }
    run(() => setOvertimeByAdmin({ promoterId: promoter.uid, promoterName: promoter.name, date: dateStr,
      hours: h, kind, note: note.trim(), byName: "Admin" }));
  };
  const kindLabel = (k) => k === OT_KINDS.HOLIDAY ? t("ot.kind_holiday")
    : k === OT_KINDS.WEEKLY_OFF ? t("ot.kind_weekly_off") : t("ot.kind_extra");

  return (
    <div style={{ borderTop: "1px solid #eee", marginTop: 14, paddingTop: 12 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>⏱ {t("ot.admin_title")}</div>
      {existing && (
        <div style={{ fontSize: 12, color: "#555", marginBottom: 8 }}>
          {t("ot.admin_current", { kind: kindLabel(existing.kind || OT_KINDS.EXTRA), h: existing.hours })}
          {" — "}<b>{existing.status === "approved" ? t("ot.st_approved") : t("ot.st_pending")}</b>
          {existing.byAdmin && <span style={{ color: "#888" }}> · {t("ot.by_admin")}</span>}
        </div>
      )}
      {err && <div className="alert alert-error" style={{ marginBottom: 8, fontSize: 12 }}>{err}</div>}
      <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
        {kinds.map(([val, label]) => (
          <button key={val} type="button" disabled={busy} onClick={() => setKind(val)}
            style={{ flex: 1, padding: "7px 6px", borderRadius: 8, cursor: "pointer", fontSize: 12,
              border: kind === val ? "2px solid #C8152A" : "1px solid #e5e7eb",
              background: kind === val ? "#FDECEA" : "#fff", fontWeight: kind === val ? 600 : 400 }}>
            {label}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <input type="number" min="0.5" max="24" step="0.5" value={hours} disabled={busy}
          onChange={e => setHours(e.target.value)} style={{ width: 80 }} />
        <span style={{ fontSize: 12, color: "#666" }}>{t("ot.hours_unit")}</span>
        <input value={note} disabled={busy} onChange={e => setNote(e.target.value)}
          placeholder={t("ot.admin_note_ph")} style={{ flex: 1, minWidth: 0 }} />
      </div>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>
        {kind === OT_KINDS.HOLIDAY ? t("ot.admin_hint_holiday")
          : kind === OT_KINDS.WEEKLY_OFF ? t("ot.admin_hint_weekly_off") : t("ot.admin_hint_extra")}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn-primary btn-sm" style={{ flex: 1 }} disabled={busy} onClick={save}>
          {busy ? t("common.saving") : existing ? t("ot.admin_update") : t("ot.admin_save")}
        </button>
        {existing?.status === "pending" && (
          <button className="btn btn-sm" disabled={busy} onClick={() => run(() => approveOvertime(existing.id, "Admin"))}>
            ✓ {t("lv.approve")}
          </button>
        )}
        {existing && (
          <button className="btn btn-sm" style={{ color: "#C8152A" }} disabled={busy}
            onClick={() => run(() => deleteOvertime(existing.id, "Admin", true))}>
            ✕ {t("ot.admin_delete")}
          </button>
        )}
      </div>
    </div>
  );
}

// Bấm vào một ô trong bảng ma trận để đánh dấu ngày nghỉ — nhanh hơn form nhập từng ngày,
// và giữ dữ liệu trong app luôn khớp với file Excel xuất ra.
function DayMarkModal({ cell, onClose, onDone, footer = null }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [workHours, setWorkHours] = useState(8);
  if (!cell) return null;
  const { promoter, dateStr, rec } = cell;
  const isMarked = rec && (rec.dayType === DAY_TYPES.PAID_LEAVE
    || rec.dayType === DAY_TYPES.ANNUAL_LEAVE || rec.dayType === DAY_TYPES.MANUAL_WORK);
  const markedLabel = rec?.dayType === DAY_TYPES.ANNUAL_LEAVE ? t("att.leave_annual")
    : rec?.dayType === DAY_TYPES.MANUAL_WORK ? `${t("att.mark_work")} (${rec.totalHours}h)`
    : rec?.dayType === DAY_TYPES.UNPAID_LEAVE ? t("lv.unpaid")
    : t("att.leave_paid");
  const pendingLeave = isPendingLeave(rec);

  const decideLeave = async (approve) => {
    setBusy(true); setErr("");
    try {
      if (approve) await approveLeave(rec.id, "Admin");
      else await rejectLeave(rec.id, "Admin");
      onDone();
      onClose();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const apply = async (dayType) => {
    setBusy(true); setErr("");
    try {
      if (rec) await deleteAttendance(rec.id);
      if (dayType === DAY_TYPES.MANUAL_WORK) {
        const h = Number(workHours) || 8;
        await setManualWorkDay({ promoterId: promoter.uid, date: dateStr, hours: h });
        addAuditLog({
          type: "attendance_checkin", promoterName: promoter.name,
          detail: `Xác nhận đủ công ${h}h cho ${promoter.name} ngày ${dateStr} (app lỗi / báo qua chat)`,
          meta: { date: dateStr, dayType, hours: h },
        });
      } else if (dayType) {
        await setPaidLeave({ promoterId: promoter.uid, date: dateStr, dayType });
        addAuditLog({
          type: "attendance_checkin", promoterName: promoter.name,
          detail: `Đánh dấu ${dayType === DAY_TYPES.ANNUAL_LEAVE ? t("att.leave_annual") : t("att.leave_paid")} cho ${promoter.name} ngày ${dateStr}`,
          meta: { date: dateStr, dayType },
        });
      }
      onDone();
      onClose();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginBottom: 4 }}>{t("att.mark_day")}</h3>
        <div style={{ fontSize: 13, color: "#666", marginBottom: 14 }}>
          {promoter.name} — {dateStr}
          {isMarked && <span style={{ color: "#15803d", fontWeight: 600 }}>{" · "}{markedLabel}</span>}
        </div>
        {err && <div className="alert alert-error" style={{ marginBottom: 10 }}>{err}</div>}

        {pendingLeave && (
          <div style={{ marginBottom: 12 }}>
            <div className="alert alert-warn" style={{ fontSize: 12, marginBottom: 8 }}>
              {t("lv.pending_hint")}
              {rec.leaveReason ? <div style={{ marginTop: 4, color: "#555" }}>“{rec.leaveReason}”</div> : null}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy} onClick={() => decideLeave(true)}>
                ✓ {t("lv.approve")}
              </button>
              <button className="btn" style={{ flex: 1, color: "#C8152A" }} disabled={busy} onClick={() => decideLeave(false)}>
                ✕ {t("lv.reject")}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy} onClick={() => apply(DAY_TYPES.MANUAL_WORK)}>
              ✅ {t("att.mark_work")}
            </button>
            <select value={workHours} onChange={e => setWorkHours(e.target.value)} disabled={busy} style={{ width: 88, fontSize: 13 }}>
              <option value={8}>8h</option>
              <option value={4}>4h</option>
            </select>
          </div>
          <div style={{ fontSize: 11, color: "#888", marginTop: -2 }}>{t("att.mark_work_note")}</div>
          <button className="btn" disabled={busy} onClick={() => apply(DAY_TYPES.ANNUAL_LEAVE)}>
            🌴 {t("att.leave_annual")}
          </button>
          <button className="btn" disabled={busy} onClick={() => apply(DAY_TYPES.PAID_LEAVE)}>
            💚 {t("att.leave_paid")}
          </button>
          {isMarked && (
            <button className="btn" disabled={busy} style={{ color: "#C8152A" }} onClick={() => apply(null)}>
              ✕ {t("att.mark_clear")}
            </button>
          )}
          <button className="btn" disabled={busy} onClick={onClose}>{t("common.cancel")}</button>
        </div>
        {rec && !isMarked && (
          <div style={{ fontSize: 11, color: "#C8152A", marginTop: 10 }}>{t("att.mark_overwrite")}</div>
        )}
        {footer}
      </div>
    </div>
  );
}

function OverviewTab({ month, promoters }) {
  const { t } = useLanguage();
  const [allRecords, setAllRecords] = useState([]);
  const [photoRecord, setPhotoRecord] = useState(null);
  const [markCell, setMarkCell] = useState(null); // { promoter, dateStr, rec }
  const [otCell, setOtCell] = useState(null);     // { promoter, dateStr } — ô đang mở ảnh chấm công
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [leaveForm, setLeaveForm] = useState({ promoterId: "", date: "", dayType: DAY_TYPES.PAID_LEAVE });
  const [eventForm, setEventForm] = useState({ promoterId: "", name: "", amount: "", date: "" });
  const [mgmtMsg, setMgmtMsg] = useState("");

  const [monthAppeals, setMonthAppeals] = useState([]);
  const [holidayDates, setHolidayDates] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [pendingOt, setPendingOt] = useState([]);
  const [monthOt, setMonthOt] = useState([]);
  const { cfgFor } = usePayrollConfig();
  const work = cfgFor(month).work;

  const reload = () => {
    getPendingLeaves().then(setPendingLeaves).catch(() => {});
    getPendingOvertime().then(setPendingOt).catch(() => {});
    getOvertimeByMonth(month).then(setMonthOt).catch(() => {});
    getAllAttendanceByMonth(month).then(setAllRecords);
    getEventBonuses(month).then(setEvents);
    getAllAppeals(month).then(setMonthAppeals).catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getAllAttendanceByMonth(month), getEventBonuses(month), getAllAppeals(month).catch(() => []),
      getHolidays().catch(() => []), getPendingLeaves().catch(() => []),
    ]).then(([r, e, ap, hs, pl]) => {
      setAllRecords(r); setEvents(e); setMonthAppeals(ap);
      setHolidayDates(hs.map(h => h.date)); setPendingLeaves(pl);
      getPendingOvertime().then(setPendingOt).catch(() => {});
      getOvertimeByMonth(month).then(setMonthOt).catch(() => {});
      setLoading(false);
    });
  }, [month]);

  // Map giải trình theo promoter + ngày để hiện trên bảng tổng quan
  const appealMap = {};
  monthAppeals.forEach(a => { appealMap[`${a.promoterId}_${a.date}`] = a; });

  const handleAddLeave = async () => {
    if (!leaveForm.promoterId || !leaveForm.date) { setMgmtMsg("Chọn promoter và ngày"); return; }
    try {
      await setPaidLeave({ promoterId: leaveForm.promoterId, date: leaveForm.date, dayType: leaveForm.dayType });
      const p = promoters.find(x => x.uid === leaveForm.promoterId);
      const leaveLabel = leaveForm.dayType === DAY_TYPES.ANNUAL_LEAVE ? t("att.leave_annual") : t("att.leave_paid");
      addAuditLog({ type: "attendance_checkin", actorName: "Admin", actorRole: "admin", promoterName: p?.name, detail: `Thêm ${leaveLabel} cho ${p?.name} ngày ${leaveForm.date}`, meta: { date: leaveForm.date, dayType: leaveForm.dayType } });
      setMgmtMsg(`✅ Đã thêm ${leaveLabel}`);
      setLeaveForm({ promoterId: "", date: "", dayType: DAY_TYPES.PAID_LEAVE });
      reload();
    } catch (err) { setMgmtMsg("Lỗi: " + err.message); }
    setTimeout(() => setMgmtMsg(""), 3000);
  };

  const handleAddEvent = async () => {
    if (!eventForm.promoterId || !eventForm.name || !eventForm.amount) { setMgmtMsg("Điền đủ thông tin sự kiện"); return; }
    await addEventBonus({ ...eventForm, month, amount: Number(eventForm.amount) });
    const p = promoters.find(x => x.uid === eventForm.promoterId);
    addAuditLog({ type: "bonus_program_added", actorName: "Admin", actorRole: "admin", detail: `Khoán sự kiện "${eventForm.name}" ${Number(eventForm.amount).toLocaleString("vi-VN")}đ cho ${p?.name}`, meta: { month } });
    setMgmtMsg("✅ Đã thêm khoán sự kiện");
    setEventForm({ promoterId: "", name: "", amount: "", date: "" });
    reload();
    setTimeout(() => setMgmtMsg(""), 3000);
  };

  const handleDeleteEvent = async (id) => { await deleteEventBonus(id); reload(); };

  const daysInMonth = getDaysInMonth(new Date(month + "-01"));
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const recordMap = {};
  allRecords.forEach(r => {
    if (!recordMap[r.promoterId]) recordMap[r.promoterId] = {};
    recordMap[r.promoterId][r.date] = r;
  });

  const todayStr = format(new Date(), "yyyy-MM-dd");

  // Today summary
  const todayRecs = allRecords.filter(r => r.date === todayStr);
  const checkedInToday = todayRecs.filter(r => r.checkinTime).length;
  const doneToday = todayRecs.filter(r => r.checkoutTime).length;
  const notYetToday = promoters.length - checkedInToday;

  const holidaySet = new Set(holidayDates);
  const otMap = {};
  monthOt.forEach(o => { otMap[`${o.promoterId}_${o.date}`] = o; });
  // key theo đơn hiện có → lưu/xóa xong khung tự nạp lại giá trị mới
  const otPanel = ({ promoter, dateStr }) => {
    const existing = otMap[`${promoter.uid}_${dateStr}`] || null;
    return (
      <OtAdminPanel key={`${promoter.uid}_${dateStr}_${existing?.hours ?? ""}_${existing?.status ?? ""}`}
        promoter={promoter} dateStr={dateStr} isHoliday={holidaySet.has(dateStr)} existing={existing}
        onDone={reload} />
    );
  };

  const getCellClass = (rec, dateStr) => {
    if (!rec && dateStr === todayStr) return "";
    const st = getAttendanceStatus({
      rec, dateStr, todayStr, isHoliday: holidaySet.has(dateStr), work,
    });
    const cls = ATT_STATUS[st]?.cls || "";
    return isPendingLeave(rec) ? `${cls} att-s-pending` : cls;
  };

  return (
    <>
      {photoRecord && <PhotoModal record={photoRecord} onClose={() => { setPhotoRecord(null); setOtCell(null); }}
        footer={otCell && otPanel(otCell)} />}
      <DayMarkModal cell={markCell} onClose={() => setMarkCell(null)} onDone={reload}
        footer={markCell && otPanel(markCell)} />

      {pendingOt.length > 0 && (
        <div className="card" style={{ marginBottom: 14, borderLeft: "3px solid #B45309" }}>
          <div className="card-title">⏱ {t("ot.pending_title")} ({pendingOt.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            {pendingOt.map(o => (
              <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                padding: "8px 10px", background: "#FAFAFA", borderRadius: 8, fontSize: 13 }}>
                <span style={{ fontWeight: 600, minWidth: 130 }}>{o.promoterName}</span>
                <span>{o.date?.split("-").reverse().join("/")}</span>
                <span className="badge badge-amber">
                  {o.kind === OT_KINDS.WEEKLY_OFF ? `${t("ot.kind_weekly_off")} · ${o.hours}h` : o.kind === OT_KINDS.HOLIDAY ? `${t("ot.kind_holiday")} · ${o.hours}h` : `+${o.hours}h`}
                </span>
                {o.reason && <span style={{ color: "#777", flex: 1 }}>“{o.reason}”</span>}
                <button className="btn btn-sm btn-primary"
                  onClick={async () => { await approveOvertime(o.id, "Admin"); reload(); }}>✓ {t("lv.approve")}</button>
                <button className="btn btn-sm" style={{ color: "#C8152A" }}
                  onClick={async () => { await deleteOvertime(o.id, "Admin", true); reload(); }}>✕ {t("lv.reject")}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendingLeaves.length > 0 && (
        <div className="card" style={{ marginBottom: 14, borderLeft: "3px solid #C8152A" }}>
          <div className="card-title">🌴 {t("lv.pending_title")} ({pendingLeaves.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            {pendingLeaves.map(lv => {
              const p = promoters.find(x => x.uid === lv.promoterId);
              const isAnnual = lv.dayType === DAY_TYPES.ANNUAL_LEAVE;
              return (
                <div key={lv.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                  padding: "8px 10px", background: "#FAFAFA", borderRadius: 8, fontSize: 13 }}>
                  <span style={{ fontWeight: 600, minWidth: 130 }}>{p?.name || lv.promoterId}</span>
                  <span>{lv.date?.split("-").reverse().join("/")}</span>
                  <span className="badge" style={{ background: isAnnual ? ATT_STATUS.annualLeave.color : ATT_STATUS.unpaidLeave.color }}>
                    {isAnnual ? t("att.leave_annual") : t("lv.unpaid")}
                  </span>
                  {lv.leaveReason && <span style={{ color: "#777", flex: 1 }}>“{lv.leaveReason}”</span>}
                  <button className="btn btn-sm btn-primary"
                    onClick={async () => { await approveLeave(lv.id, "Admin"); reload(); }}>✓ {t("lv.approve")}</button>
                  <button className="btn btn-sm" style={{ color: "#C8152A" }}
                    onClick={async () => { await rejectLeave(lv.id, "Admin"); reload(); }}>✕ {t("lv.reject")}</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {month === format(new Date(), "yyyy-MM") && (
        <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <div className="metric-card" style={{ flex: "1 1 140px", minWidth: 130 }}>
            <div className="metric-label">Đã check-in hôm nay</div>
            <div className="metric-value" style={{ color: "#3B6D11" }}>{checkedInToday}</div>
            <div className="metric-sub">/ {promoters.length} promoter</div>
          </div>
          <div className="metric-card" style={{ flex: "1 1 140px", minWidth: 130 }}>
            <div className="metric-label">Đã hoàn thành ca</div>
            <div className="metric-value">{doneToday}</div>
            <div className="metric-sub">đã check-out</div>
          </div>
          <div className="metric-card" style={{ flex: "1 1 140px", minWidth: 130 }}>
            <div className="metric-label">Chưa check-in</div>
            <div className="metric-value" style={{ color: notYetToday > 0 ? "#A32D2D" : "#3B6D11" }}>{notYetToday}</div>
            <div className="metric-sub">người vắng mặt</div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-title" style={{ marginBottom: 10 }}>{t("attm.title")}</div>
        {mgmtMsg && <div className={`alert ${mgmtMsg.startsWith("✅") ? "alert-success" : "alert-error"}`} style={{ marginBottom: 10 }}>{mgmtMsg}</div>}
        <div className="grid-2" style={{ gap: 16 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t("attm.leave_title")}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <select value={leaveForm.promoterId} onChange={e => setLeaveForm(f => ({ ...f, promoterId: e.target.value }))} style={{ flex: "1 1 140px", fontSize: 13 }}>
                <option value="">— Promoter —</option>
                {promoters.map(p => <option key={p.uid} value={p.uid}>{p.name}</option>)}
              </select>
              <select value={leaveForm.dayType} onChange={e => setLeaveForm(f => ({ ...f, dayType: e.target.value }))} style={{ flex: "1 1 130px", fontSize: 13 }}>
                <option value={DAY_TYPES.PAID_LEAVE}>{t("att.leave_paid")}</option>
                <option value={DAY_TYPES.ANNUAL_LEAVE}>{t("att.leave_annual")}</option>
              </select>
              <input type="date" value={leaveForm.date} onChange={e => setLeaveForm(f => ({ ...f, date: e.target.value }))} style={{ fontSize: 13 }} />
              <button className="btn btn-primary btn-sm" onClick={handleAddLeave}>+ Thêm</button>
            </div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{t("attm.leave_note")}</div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t("attm.event_title")}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <select value={eventForm.promoterId} onChange={e => setEventForm(f => ({ ...f, promoterId: e.target.value }))} style={{ flex: "1 1 120px", fontSize: 13 }}>
                <option value="">— Promoter —</option>
                {promoters.map(p => <option key={p.uid} value={p.uid}>{p.name}</option>)}
              </select>
              <input placeholder={t("attm.event_name")} value={eventForm.name} onChange={e => setEventForm(f => ({ ...f, name: e.target.value }))} style={{ flex: "1 1 120px", fontSize: 13 }} />
              <input type="number" placeholder={t("attm.amount")} value={eventForm.amount} onChange={e => setEventForm(f => ({ ...f, amount: e.target.value }))} style={{ flex: "1 1 100px", fontSize: 13 }} />
              <button className="btn btn-primary btn-sm" onClick={handleAddEvent}>+ Thêm</button>
            </div>
            {events.length > 0 && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                {events.map(ev => {
                  const p = promoters.find(x => x.uid === ev.promoterId);
                  return (
                    <div key={ev.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, background: "#f7f7f7", borderRadius: 6, padding: "4px 8px" }}>
                      <span>{p?.name} — {ev.name}: <strong>{Number(ev.amount).toLocaleString("vi-VN")}đ</strong></span>
                      <button className="btn btn-sm btn-danger" style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => handleDeleteEvent(ev.id)}>✕</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ color: "#aaa", padding: 20, textAlign: "center" }}>{t("common.loading")}</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="table-scroll" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#fafafa" }}>
                  <th style={{ minWidth: 140, padding: "8px 12px", textAlign: "left", position: "sticky", left: 0, background: "#fafafa", borderBottom: "0.5px solid rgba(0,0,0,0.08)", fontWeight: 500, fontSize: 11, color: "#888" }}>Promoter</th>
                  {days.map(d => {
                    const dateStr = `${month}-${String(d).padStart(2, "0")}`;
                    const isToday = dateStr === todayStr;
                    return (
                      <th key={d} style={{ width: 28, padding: "6px 2px", textAlign: "center", fontWeight: isToday ? 600 : 400, color: isToday ? "#C8152A" : "#aaa", fontSize: 10, borderBottom: "0.5px solid rgba(0,0,0,0.08)" }}>{d}</th>
                    );
                  })}
                  <th style={{ width: 50, padding: "8px 8px", textAlign: "center", borderBottom: "0.5px solid rgba(0,0,0,0.08)", fontWeight: 500, fontSize: 11, color: "#888" }}>Công</th>
                  <th style={{ width: 60, padding: "8px 8px", textAlign: "center", borderBottom: "0.5px solid rgba(0,0,0,0.08)", fontWeight: 500, fontSize: 11, color: "#888" }}>Giờ</th>
                </tr>
              </thead>
              <tbody>
                {promoters.map(p => {
                  const pMap = recordMap[p.uid] || {};
                  const daysWorked = countPaidDays({
                    records: Object.values(pMap), holidays: holidayDates.map(date => ({ date })),
                    month, startDate: p.startDate, work,
                    overtime: monthOt.filter(o => o.promoterId === p.uid),
                  });
                  const totalHours = Object.values(pMap).reduce((a, r) => a + (r.totalHours || 0), 0);
                  return (
                    <tr key={p.uid} style={{ borderBottom: "0.5px solid rgba(0,0,0,0.05)" }}>
                      <td style={{ position: "sticky", left: 0, background: "#fff", padding: "8px 12px", borderBottom: "0.5px solid rgba(0,0,0,0.05)" }}>
                        <div style={{ fontWeight: 500, fontSize: 12 }}>{p.name}</div>
                        <div style={{ fontSize: 10, color: "#aaa" }}>{p.shopName}</div>
                      </td>
                      {days.map(d => {
                        const dateStr = `${month}-${String(d).padStart(2, "0")}`;
                        const rec = pMap[dateStr];
                        const cls = getCellClass(rec, dateStr);
                        const isToday = dateStr === todayStr;
                        const isFuture = dateStr > todayStr;
                        const ap = appealMap[`${p.uid}_${dateStr}`];
                        const apLabel = ap ? (APPEAL_STATUS_VI[ap.status]?.label || ap.status) : "";
                        const isLeaveRec = !!rec && (rec.dayType === DAY_TYPES.PAID_LEAVE
                          || rec.dayType === DAY_TYPES.ANNUAL_LEAVE || rec.dayType === DAY_TYPES.MANUAL_WORK);
                        const baseTitle = isLeaveRec
                          ? (rec.dayType === DAY_TYPES.ANNUAL_LEAVE ? t("att.leave_annual")
                            : rec.dayType === DAY_TYPES.MANUAL_WORK ? `${t("att.mark_work")} — ${rec.totalHours}h`
                            : t("att.leave_paid"))
                          : rec ? `${rec.checkinTime || ""}–${rec.checkoutTime || "?"} (${rec.totalHours || "?"}h)`
                          : isFuture ? t("att.mark_hint") : `Vắng — ${t("att.mark_hint")}`;
                        const otRec = otMap[`${p.uid}_${dateStr}`];
                        const otTitle = otRec ? `\n⏱ ${otRec.kind === OT_KINDS.HOLIDAY ? t("ot.kind_holiday")
                          : otRec.kind === OT_KINDS.WEEKLY_OFF ? t("ot.kind_weekly_off") : t("ot.kind_extra")} ${otRec.hours}h — ${
                          otRec.status === "approved" ? t("ot.st_approved") : t("ot.st_pending")}` : "";
                        return (
                          <td key={d} style={{ padding: "6px 2px", textAlign: "center" }}>
                            <div
                              className={`att-matrix-cell ${cls} ${isToday ? "att-matrix-today" : ""} ${otRec ? (otRec.status === "approved" ? "att-ot-mark" : "att-ot-mark att-ot-pending") : ""}`}
                              onClick={() => {
                                if (rec && !isLeaveRec) { setPhotoRecord(rec); setOtCell({ promoter: p, dateStr }); }
                                else setMarkCell({ promoter: p, dateStr, rec: rec || null });
                              }}
                              title={baseTitle + (ap ? `\nGiải trình (${apLabel}): ${ap.reason || ""}` : "") + otTitle}
                              style={{ cursor: "pointer", boxShadow: ap ? "inset 0 0 0 2px #7C3AED" : undefined }}
                            />
                          </td>
                        );
                      })}
                      <td style={{ textAlign: "center", fontWeight: 500, padding: "8px 8px" }}>{daysWorked}</td>
                      <td style={{ textAlign: "center", color: "#888", padding: "8px 8px" }}>{totalHours.toFixed(1)}h</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 14, padding: "10px 14px", borderTop: "0.5px solid rgba(0,0,0,0.06)", fontSize: 11, color: "#888", flexWrap: "wrap" }}>
            {["full", "missing", "short", "holiday", "annualLeave", "unpaidLeave", "weeklyOff"].map(k => (
              <span key={k}><span className={`att-legend ${ATT_STATUS[k].cls}`} /> {t(`att.st_${k}`)}</span>
            ))}
            <span><span className="att-legend att-s-pending" style={{ background: "#eee" }} /> {t("lv.pending_short")}</span>
            <span><span className="att-legend" style={{ boxShadow: "inset 0 0 0 2px #7C3AED" }} /> {t("ah.legend_appeal")}</span>
          </div>
        </div>
      )}
    </>
  );
}

function DetailTab({ month, setMonth, promoters }) {
  const { t } = useLanguage();
  const [selected, setSelected] = useState("");
  const [records, setRecords] = useState([]);
  const [photoRecord, setPhotoRecord] = useState(null);

  useEffect(() => {
    if (promoters.length && !selected) setSelected(promoters[0].uid);
  }, [promoters]);

  useEffect(() => {
    if (!selected) return;
    getAttendanceByMonth(selected, month).then(setRecords);
  }, [selected, month]);

  const selectedPromoter = promoters.find(p => p.uid === selected);
  const recordMap = {};
  records.forEach(r => { recordMap[r.date] = r; });

  const workedRecs = records.filter(isCreditedDay);
  const daysWorked = workedRecs.reduce((a, r) => a + (r.dayCredit ?? 1), 0);
  const holidayDays = workedRecs.filter(r => r.isHoliday).reduce((a, r) => a + (r.dayCredit ?? 1), 0);
  const totalHours = records.reduce((a, r) => a + (r.totalHours || 0), 0);
  const daysInMonth = getDaysInMonth(new Date(month + "-01"));
  const firstDay = new Date(month + "-01").getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const todayStr = format(new Date(), "yyyy-MM-dd");

  return (
    <>
      {photoRecord && <PhotoModal record={photoRecord} onClose={() => setPhotoRecord(null)} />}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <select value={selected} onChange={e => setSelected(e.target.value)} style={{ width: 200 }}>
          {promoters.map(p => <option key={p.uid} value={p.uid}>{p.name}</option>)}
        </select>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 160 }} />
        <button className="btn" onClick={() => selectedPromoter && exportToCSV(selectedPromoter.name, month, records)} disabled={!records.length}>
          ⬇ Xuất Excel
        </button>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div className="card-title">{t("att.calendar")} {month.slice(5)}/{month.slice(0,4)}</div>
            <div style={{ display: "flex", gap: 6 }}>
              <span className="badge badge-green">● {t("att.full")}</span>
              <span className="badge badge-amber">● {t("att.holiday")}</span>
              <span className="badge badge-gray">● {t("att.absent")}</span>
            </div>
          </div>
          <div className="att-grid">
            {["T2","T3","T4","T5","T6","T7","CN"].map(d => (
              <div key={d} className="att-day-header">{d}</div>
            ))}
            {Array(offset).fill(null).map((_, i) => <div key={"e"+i} className="att-day att-empty" />)}
            {days.map(d => {
              const dateStr = `${month}-${String(d).padStart(2, "0")}`;
              const rec = recordMap[dateStr];
              const today = dateStr === todayStr;
              let cls = "att-day ";
              if (rec?.checkoutTime) cls += rec.isHoliday ? "att-holiday" : "att-present";
              else if (rec?.checkinTime) cls += "att-present";
              else if (dateStr < todayStr) cls += "att-absent";
              else cls += "att-empty";
              if (today) cls += " att-today";
              return (
                <div key={d} className={cls}
                  title={rec ? `${rec.checkinTime||""}–${rec.checkoutTime||""}` : ""}
                  onClick={() => rec && setPhotoRecord(rec)}
                  style={{ cursor: rec ? "pointer" : "default" }}
                >{d}</div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>{t("att.summary")}</div>
          <div className="salary-breakdown" style={{ marginBottom: 14 }}>
            <div className="salary-row"><span>{t("att.26_days")}</span><span>26 ngày</span></div>
            <div className="salary-row"><span>{t("att.days_worked")}</span><span>{daysWorked} công</span></div>
            <div className="salary-row"><span>{t("att.holiday_days")}</span><span>{holidayDays} công</span></div>
            <div className="salary-row"><span>{t("att.total_hours")}</span><span>{totalHours.toFixed(1)}h</span></div>
          </div>
          <div style={{ overflowY: "auto", maxHeight: 280 }}>
            <table>
              <thead><tr><th>Ngày</th><th>{t("att.checkin_time")}</th><th>{t("att.checkout_time")}</th><th>{t("att.hours")}</th><th>{t("common.note")}</th></tr></thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id} onClick={() => setPhotoRecord(r)} style={{ cursor: "pointer" }}>
                    <td>{r.date.slice(8)}/{r.date.slice(5,7)}</td>
                    <td>{r.checkinTime || "—"}</td>
                    <td>{r.checkoutTime || "—"}</td>
                    <td>{r.totalHours ? r.totalHours + "h" : "—"}</td>
                    <td style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {r.isHoliday && <span className="badge badge-amber">Lễ ×3</span>}
                      {r.totalHours && r.totalHours < 7 && !r.appealApproved && <span className="badge badge-red">Thiếu</span>}
                      {r.appealApproved && <span className="badge badge-blue">Giải trình</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// Badge trạng thái + xuất CSV lịch sử giải trình (dùng chung admin/supervisor)
export const APPEAL_STATUS_VI = {
  pending: { label: "Chờ duyệt", badge: "badge-amber" },
  approved: { label: "Đã duyệt", badge: "badge-green" },
  rejected: { label: "Từ chối", badge: "badge-red" },
};

export const exportAppealsCSV = (list, suffix = "") => {
  const header = ["Ngày", "Promoter", "Lý do", "Trạng thái", "Ghi chú admin", "Gửi lúc", "Xử lý lúc"];
  const fmtTs = (ts) => (ts?.seconds ? format(new Date(ts.seconds * 1000), "dd/MM/yyyy HH:mm") : "");
  const rows = list.map(a => [
    a.date, a.promoterName, a.reason || "",
    APPEAL_STATUS_VI[a.status]?.label || a.status,
    a.adminNote || "", fmtTs(a.submittedAt), fmtTs(a.reviewedAt),
  ]);
  const csv = [header, ...rows]
    .map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `giai-trinh${suffix ? "_" + suffix : ""}.csv`; a.click();
  URL.revokeObjectURL(url);
};

// Bảng lịch sử giải trình (chỉ xem) — dùng chung admin/supervisor
export function AppealHistoryView({ appeals, loading, filterMonth, setFilterMonth }) {
  return (
    <div className="card">
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ width: 150 }} />
        {filterMonth && <button className="btn btn-sm" onClick={() => setFilterMonth("")}>✕</button>}
        <span style={{ fontSize: 12, color: "#888" }}>{appeals.length} giải trình</span>
        <div style={{ flex: 1 }} />
        <button className="btn btn-sm" onClick={() => exportAppealsCSV(appeals, filterMonth || "tat-ca")} disabled={appeals.length === 0}>
          ⬇ Xuất CSV
        </button>
      </div>
      {loading ? (
        <div style={{ color: "#aaa", padding: 20, textAlign: "center" }}>Đang tải...</div>
      ) : appeals.length === 0 ? (
        <div style={{ color: "#aaa", padding: 24, textAlign: "center" }}>Không có giải trình nào</div>
      ) : (
        <table className="table-card">
          <thead>
            <tr><th>Ngày</th><th>Promoter</th><th>Lý do</th><th>Trạng thái</th><th>Ghi chú admin</th><th>Xử lý lúc</th></tr>
          </thead>
          <tbody>
            {appeals.map(a => {
              const st = APPEAL_STATUS_VI[a.status] || { label: a.status, badge: "badge-gray" };
              return (
                <tr key={a.id}>
                  <td data-label="Ngày" style={{ whiteSpace: "nowrap", fontWeight: 500 }}>{a.date}</td>
                  <td data-label="Promoter">{a.promoterName}</td>
                  <td data-label="Lý do" style={{ maxWidth: 260 }}><span style={{ fontSize: 12, color: "#555" }}>{a.reason}</span></td>
                  <td data-label="Trạng thái"><span className={`badge ${st.badge}`}>{st.label}</span></td>
                  <td data-label="Ghi chú" style={{ fontSize: 12, color: "#888" }}>{a.adminNote || "—"}</td>
                  <td data-label="Xử lý lúc" style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap" }}>
                    {a.reviewedAt?.seconds ? format(new Date(a.reviewedAt.seconds * 1000), "dd/MM/yyyy HH:mm") : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AppealsTab({ onCountChange }) {
  const { t } = useLanguage();
  const [mode, setMode] = useState("pending"); // "pending" | "history"
  const [appeals, setAppeals] = useState([]);
  const [rejectNote, setRejectNote] = useState({});
  const [loading, setLoading] = useState(true);
  // History state
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histMonth, setHistMonth] = useState("");

  const load = () => {
    setLoading(true);
    getPendingAppeals().then(a => { setAppeals(a); onCountChange(a.length); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (mode !== "history") return;
    setHistLoading(true);
    getAllAppeals(histMonth || null).then(h => { setHistory(h); setHistLoading(false); });
  }, [mode, histMonth]);

  const handleApprove = async (appeal) => {
    await approveAppeal(appeal);
    addAuditLog({ type: "appeal_approved", actorName: "Admin", actorRole: "admin", promoterName: appeal.promoterName, detail: `Duyệt giải trình của ${appeal.promoterName} ngày ${appeal.date}`, meta: { date: appeal.date } });
    load();
  };

  const handleReject = async (appeal) => {
    await rejectAppeal(appeal.id, rejectNote[appeal.id] || "");
    addAuditLog({ type: "appeal_rejected", actorName: "Admin", actorRole: "admin", promoterName: appeal.promoterName, detail: `Từ chối giải trình của ${appeal.promoterName} ngày ${appeal.date}`, meta: { date: appeal.date } });
    load();
  };

  const ModeSwitch = (
    <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
      <button className={"btn btn-sm" + (mode === "pending" ? " btn-primary" : "")} onClick={() => setMode("pending")}>
        {t("ah.pending_mode")} {appeals.length > 0 ? `(${appeals.length})` : ""}
      </button>
      <button className={"btn btn-sm" + (mode === "history" ? " btn-primary" : "")} onClick={() => setMode("history")}>
        {t("ah.history_mode")}
      </button>
    </div>
  );

  if (mode === "history") return (
    <>
      {ModeSwitch}
      <AppealHistoryView appeals={history} loading={histLoading} filterMonth={histMonth} setFilterMonth={setHistMonth} />
    </>
  );

  if (loading) return <>{ModeSwitch}<div style={{ color: "#aaa", padding: 20, textAlign: "center" }}>{t("common.loading")}</div></>;
  if (!appeals.length) return (
    <>
      {ModeSwitch}
      <div className="card" style={{ textAlign: "center", padding: 32, color: "#aaa" }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>✓</div>
        {t("att.no_appeals")}
      </div>
    </>
  );

  return (
    <>
    {ModeSwitch}
    <div className="card">
      <table className="table-card">
        <thead>
          <tr>
            <th>Ngày</th>
            <th>Promoter</th>
            <th>{t("att.reason")}</th>
            <th>{t("att.submitted_at")}</th>
            <th style={{ width: 260 }}></th>
          </tr>
        </thead>
        <tbody>
          {appeals.map(a => (
            <tr key={a.id}>
              <td data-label="Ngày" style={{ whiteSpace: "nowrap", fontWeight: 500 }}>{a.date?.slice(8)}/{a.date?.slice(5,7)}</td>
              <td data-label="Promoter">{a.promoterName}</td>
              <td data-label="Lý do" style={{ maxWidth: 240 }}><span style={{ fontSize: 12, color: "#555" }}>{a.reason}</span></td>
              <td data-label="Gửi lúc" style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap" }}>
                {a.submittedAt?.seconds ? format(new Date(a.submittedAt.seconds * 1000), "dd/MM HH:mm") : "—"}
              </td>
              <td data-label="">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => handleApprove(a)}>✓ {t("common.approve_short")}</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleReject(a)}>✕ {t("common.reject_short")}</button>
                  </div>
                  <input
                    placeholder={t("att.reject_note")}
                    value={rejectNote[a.id] || ""}
                    onChange={e => setRejectNote(n => ({ ...n, [a.id]: e.target.value }))}
                    style={{ fontSize: 12, padding: "4px 8px" }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
}

export default function AdminAttendance() {
  const { t } = useLanguage();
  const [tab, setTab] = useState("overview");
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [promoters, setPromoters] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportingXls, setExportingXls] = useState(false);
  const { cfgFor } = usePayrollConfig();

  useEffect(() => {
    getAllPromoters().then(setPromoters);
    getPendingAppeals().then(a => setPendingCount(a.length));
  }, []);

  const handleExportAll = async () => {
    setExporting(true);
    await exportAllToCSV(promoters, month, getAllAttendanceByMonth);
    setExporting(false);
  };

  // Bảng ma trận ngày × promoter, có màu — dùng để rà soát công cuối tháng
  const handleExportExcel = async () => {
    setExportingXls(true);
    try {
      const [records, holidays, overtime] = await Promise.all([
        getAllAttendanceByMonth(month),
        getHolidays().catch(() => []),
        getOvertimeByMonth(month).catch(() => []),
      ]);
      await exportAttendanceMatrixXLSX({ promoters, month, records, holidays, overtime, work: cfgFor(month).work });
    } catch (err) {
      alert("Lỗi xuất Excel: " + err.message);
    }
    setExportingXls(false);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{t("att.title")}</h1>
          <p>{t("att.subtitle")}</p>
        </div>
        {tab !== "appeals" && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 160 }} />
            <button className="btn" onClick={handleExportExcel} disabled={exportingXls || !promoters.length}>
              {exportingXls ? t("common.exporting") : t("att.export_excel")}
            </button>
            <button className="btn btn-primary" onClick={handleExportAll} disabled={exporting || !promoters.length}>
              {exporting ? t("common.exporting") : t("common.exportAll")}
            </button>
          </div>
        )}
      </div>

      <div className="tabs">
        <div className={`tab ${tab === "overview" ? "active" : ""}`} onClick={() => setTab("overview")}>
          {t("att.tab_overview")}
        </div>
        <div className={`tab ${tab === "detail" ? "active" : ""}`} onClick={() => setTab("detail")}>
          {t("att.tab_detail")}
        </div>
        <div className={`tab ${tab === "appeals" ? "active" : ""}`} onClick={() => setTab("appeals")}>
          {t("att.tab_appeals")} {pendingCount > 0 && <span className="badge badge-red" style={{ marginLeft: 6 }}>{pendingCount}</span>}
        </div>
      </div>

      {tab === "overview" && <OverviewTab month={month} promoters={promoters} />}
      {tab === "detail" && <DetailTab month={month} setMonth={setMonth} promoters={promoters} />}
      {tab === "appeals" && <AppealsTab onCountChange={setPendingCount} />}
    </>
  );
}
